import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Upload,
  Download,
  AlertTriangle,
  CheckCircle,
  Loader2,
  PackagePlus,
  FileSpreadsheet,
  X,
  XCircle,
  Ban,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";
import EmptyState from "../../components/system/EmptyState";

/**
 * Phase 2E S7 / S7.5 / S8 / S9 — Operator Catalog Import Wizard v1.
 *
 * Flow:
 *   0. Upload  — file or paste CSV. Inline template preview + download.
 *   1. Map    — column-mapping UI shown only when auto-detect is incomplete.
 *   2. Validate — backend classification + client-side invalid-row listing.
 *   3. Preview  — backend-staged rows; operator can cancel here.
 *   4. Commit   — confirmation modal (named target, checklist, checkbox).
 *   5. Done     — success card + quick links.
 */

// Backend-supported fields (S6 ``_ManualRowIn`` contract).
const REQUIRED_FIELDS = ["barcode", "product_name", "department_name_raw"];
const OPTIONAL_FIELDS = [
  "internal_code",
  "source_item_id",
  "category",
  "subcategory",
  "image_url",
];
const ALL_FIELDS = [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS];

// Header → field aliases (case/whitespace-insensitive on the LHS).
const FIELD_ALIASES = {
  barcode: ["barcode", "upc", "item code", "item_code", "itemcode", "sku"],
  product_name: [
    "product_name",
    "product name",
    "product",
    "item name",
    "item_name",
    "description",
    "name",
  ],
  department_name_raw: [
    "department_name_raw",
    "department",
    "dept",
    "department_name",
  ],
  internal_code: ["internal_code", "internal code"],
  source_item_id: ["source_item_id", "source item id", "source_id"],
  category: ["category"],
  subcategory: ["subcategory", "sub category", "sub_category"],
  image_url: ["image_url", "image url", "image", "image_link"],
};

function normHeader(h) {
  return String(h || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function autoDetectMapping(headers) {
  const mapping = {};
  const used = new Set();
  for (const h of headers) {
    const norm = normHeader(h);
    let chosen = "ignore";
    for (const [field, aliases] of Object.entries(FIELD_ALIASES)) {
      if (used.has(field)) continue;
      if (aliases.includes(norm)) {
        chosen = field;
        used.add(field);
        break;
      }
    }
    mapping[h] = chosen;
  }
  return mapping;
}

function mappingMissingRequired(mapping) {
  const mapped = new Set(Object.values(mapping));
  return REQUIRED_FIELDS.filter((f) => !mapped.has(f));
}

// Minimal RFC-4180 CSV parser (handles quotes + escaped quotes + embedded
// commas/newlines). Avoids pulling in a dependency.
function parseCsvText(text) {
  const out = [];
  let row = [];
  let cell = "";
  let inQuotes = false;
  const s = String(text || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (inQuotes) {
      if (ch === '"') {
        if (s[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ",") {
        row.push(cell);
        cell = "";
      } else if (ch === "\n") {
        row.push(cell);
        out.push(row);
        row = [];
        cell = "";
      } else {
        cell += ch;
      }
    }
  }
  // Flush trailing partial row.
  if (cell.length || row.length) {
    row.push(cell);
    out.push(row);
  }
  // Drop entirely-empty trailing rows.
  while (out.length && out[out.length - 1].every((c) => c === "")) out.pop();
  if (!out.length) return { headers: [], rows: [], error: "Empty CSV" };
  const headers = out[0].map((h) => String(h).trim());
  if (out.length < 2)
    return {
      headers,
      rows: [],
      error: "CSV needs at least one data row after the header.",
    };
  const rows = out.slice(1).map((cells) => {
    const obj = {};
    headers.forEach((h, idx) => {
      obj[h] = cells[idx] ?? "";
    });
    return obj;
  });
  return { headers, rows, error: null };
}

function applyMapping(rawRows, mapping) {
  return rawRows.map((row) => {
    const out = {};
    for (const [origHdr, field] of Object.entries(mapping)) {
      if (field !== "ignore") {
        out[field] = (row[origHdr] ?? "").toString().trim();
      }
    }
    return out;
  });
}

// Cheap client-side invalid-row detection. Mirrors the backend rules for
// the cases we can check without a server round-trip. (The authoritative
// classification still comes from validate/preview.)
function clientInvalidRows(normalizedRows, deptNamesLower) {
  const out = [];
  normalizedRows.forEach((r, idx) => {
    const errors = [];
    if (!r.barcode && !r.product_name && !r.internal_code) {
      errors.push("row is empty");
    }
    if (!r.product_name) errors.push("product_name is missing");
    if (!r.department_name_raw) errors.push("department_name_raw is missing");
    else if (
      deptNamesLower.size &&
      !deptNamesLower.has(r.department_name_raw.toLowerCase())
    ) {
      errors.push(
        `department "${r.department_name_raw}" is not defined for this tenant`,
      );
    }
    if (errors.length) out.push({ row: r, line: idx + 2, errors });
  });
  return out;
}

function escapeCsvCell(val) {
  const s = String(val ?? "");
  if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function makeCsv(headers, rows) {
  return (
    [
      headers.join(","),
      ...rows.map((r) => headers.map((h) => escapeCsvCell(r[h])).join(",")),
    ].join("\n") + "\n"
  );
}

function downloadBlob(text, filename, mime = "text/csv;charset=utf-8;") {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function Stepper({ step, hasMapStep }) {
  const steps = ["Upload"];
  if (hasMapStep) steps.push("Map");
  steps.push("Validate", "Preview", "Commit", "Done");
  return (
    <div
      className="flex items-center gap-2 mb-6 overflow-x-auto"
      data-testid="wizard-stepper"
    >
      {steps.map((label, idx) => {
        const active = idx === step;
        const done = idx < step;
        return (
          <React.Fragment key={label}>
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-button font-mono text-xs uppercase tracking-wider whitespace-nowrap ${
                active
                  ? "bg-ink text-white"
                  : done
                  ? "bg-emerald-100 text-emerald-900"
                  : "bg-paper-sunk text-muted-foreground"
              }`}
              data-testid={`wizard-step-${idx}`}
            >
              <span className="font-bold">{idx + 1}.</span> {label}
            </div>
            {idx < steps.length - 1 && (
              <span className="text-muted-foreground">→</span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function ClassificationBadge({ kind }) {
  const map = {
    add: "bg-emerald-100 text-emerald-900",
    update: "bg-amber-100 text-amber-900",
    unchanged: "bg-paper-sunk text-ink-2",
    needs_review: "bg-amber-100 text-amber-900",
    rejected: "bg-rose-100 text-rose-900",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-button font-mono text-[10px] uppercase tracking-wider ${
        map[kind] || "bg-paper-sunk text-ink-2"
      }`}
      data-testid={`row-classification-${kind}`}
    >
      {kind || "—"}
    </span>
  );
}

function CountTile({ label, value, tone, testid }) {
  const toneClass =
    tone === "ok"
      ? "text-emerald-700"
      : tone === "warn"
      ? "text-amber-700"
      : tone === "danger"
      ? "text-rose-700"
      : "text-ink";
  return (
    <div className="bg-paper-2 rounded-card p-3" data-testid={testid}>
      <div className="data-sku mb-1">{label}</div>
      <div
        className={`font-display font-black text-xl tabular-nums ${toneClass}`}
      >
        {value ?? 0}
      </div>
    </div>
  );
}

export default function CatalogImportWizardPage() {
  const { cid } = useParams();
  const nav = useNavigate();

  // 0=upload, 1=map, 2=validate, 3=preview, 4=commit, 5=done
  // When auto-detect succeeds, we skip 1 and the stepper hides it.
  const [step, setStep] = useState(0);
  const [hasMapStep, setHasMapStep] = useState(false);

  const [company, setCompany] = useState(null);
  const [companyError, setCompanyError] = useState(null);
  const [tenantDepts, setTenantDepts] = useState([]);

  const [csvText, setCsvText] = useState("");
  const [parsed, setParsed] = useState({
    headers: [],
    rows: [],
    error: null,
  });
  const [mapping, setMapping] = useState({});

  const [validateResult, setValidateResult] = useState(null);
  const [clientInvalid, setClientInvalid] = useState([]);

  const [previewRun, setPreviewRun] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);

  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAcknowledged, setConfirmAcknowledged] = useState(false);

  const [commitResult, setCommitResult] = useState(null);
  const [busy, setBusy] = useState(false);

  // ---- Effects ----

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/platform/companies/${cid}`)
      .then((r) => {
        if (!cancelled) setCompany(r.data.company || r.data);
      })
      .catch((err) => {
        if (!cancelled) setCompanyError(formatApiErrorDetail(err));
      });
    api
      .get(`/platform/companies/${cid}/departments`)
      .then((r) => {
        if (!cancelled) setTenantDepts(r.data.departments || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [cid]);

  const deptNamesLower = useMemo(
    () =>
      new Set(
        tenantDepts.map((d) => String(d.name || "").toLowerCase()).filter(Boolean),
      ),
    [tenantDepts],
  );

  // ---- Derived values ----

  const templateCsv = useMemo(() => {
    const headers = ALL_FIELDS;
    const seed = tenantDepts.map((d) => d.name).filter(Boolean);
    const dnames = seed.length ? seed : ["Cheese", "Bakery", "Deli"];
    const sample = [0, 1, 2].map((i) => ({
      barcode: `841007600000${i + 1}`,
      product_name: `Example product ${i + 1}`,
      department_name_raw: dnames[i % dnames.length],
      internal_code: `INT-00${i + 1}`,
      source_item_id: `SRC-00${i + 1}`,
      category: ["Refrigerated", "Shelf-stable", "Frozen"][i],
      subcategory: i === 0 ? "Aged cheese" : "",
      image_url: "",
    }));
    return makeCsv(headers, sample);
  }, [tenantDepts]);

  const normalizedRows = useMemo(
    () => (parsed.rows.length ? applyMapping(parsed.rows, mapping) : []),
    [parsed.rows, mapping],
  );

  // ---- Handlers ----

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(String(ev.target?.result || ""));
    reader.readAsText(f);
  };

  const handleDownloadTemplate = useCallback(() => {
    downloadBlob(templateCsv, `shelflife_catalog_template_${cid}.csv`);
    toast.success("CSV template downloaded.");
  }, [templateCsv, cid]);

  const handleParseAndAdvance = useCallback(() => {
    const p = parseCsvText(csvText);
    setParsed(p);
    if (p.error) {
      toast.error(p.error);
      return;
    }
    const auto = autoDetectMapping(p.headers);
    setMapping(auto);
    const missing = mappingMissingRequired(auto);
    if (missing.length) {
      setHasMapStep(true);
      setStep(1);
      toast.info(
        `Map required column${missing.length === 1 ? "" : "s"}: ${missing.join(", ")}`,
      );
    } else {
      setHasMapStep(false);
      // Skip directly to validate. Pass mapping explicitly because the
      // setMapping(auto) state update has not flushed yet.
      handleValidate(applyMapping(p.rows, auto), auto);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvText]);

  const handleValidate = useCallback(
    async (rowsArg, mappingArg) => {
      const rows = rowsArg || normalizedRows;
      const effMapping = mappingArg || mapping;
      if (!rows.length) {
        toast.error("No rows to validate.");
        return;
      }
      const missing = mappingMissingRequired(effMapping);
      if (missing.length) {
        toast.error(`Map required column(s): ${missing.join(", ")}`);
        return;
      }
      setBusy(true);
      try {
        const r = await api.post(
          `/platform/companies/${cid}/catalog/imports/validate`,
          { source: "manual", rows },
        );
        setValidateResult(r.data);
        setClientInvalid(clientInvalidRows(rows, deptNamesLower));
        setStep(hasMapStep ? 2 : 2);
        toast.success(`Validated ${r.data.row_count} rows.`);
      } catch (err) {
        toast.error(formatApiErrorDetail(err));
      } finally {
        setBusy(false);
      }
    },
    [normalizedRows, mapping, cid, deptNamesLower, hasMapStep],
  );

  const handlePreview = useCallback(async () => {
    if (!normalizedRows.length) return;
    setBusy(true);
    try {
      const r = await api.post(
        `/platform/companies/${cid}/catalog/imports/preview`,
        { source: "manual", rows: normalizedRows },
      );
      setPreviewRun(r.data.run);
      const detail = await api.get(
        `/platform/companies/${cid}/catalog/imports/${r.data.run.id}`,
      );
      setPreviewRows(detail.data.rows || []);
      setStep(hasMapStep ? 3 : 3);
      if (r.data.idempotent_replay) {
        toast.info("Replayed existing preview run (idempotent).");
      } else {
        toast.success(`Preview created. ${detail.data.row_count} rows staged.`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setBusy(false);
    }
  }, [normalizedRows, cid, hasMapStep]);

  const handleCancelRun = useCallback(async () => {
    if (!previewRun) return;
    if (
      !window.confirm(
        `Cancel run ${previewRun.id}? This run cannot be committed afterwards.`,
      )
    )
      return;
    setBusy(true);
    try {
      await api.post(
        `/platform/companies/${cid}/catalog/imports/${previewRun.id}/cancel`,
        {},
      );
      toast.success("Run cancelled.");
      // Navigate to imports list — no point keeping the cancelled-run
      // preview open.
      nav(`/platform/companies/${cid}/catalog/imports/${previewRun.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setBusy(false);
    }
  }, [previewRun, cid, nav]);

  const openCommitModal = () => {
    setConfirmAcknowledged(false);
    setShowConfirm(true);
  };

  const handleCommit = useCallback(async () => {
    if (!previewRun) return;
    if (!confirmAcknowledged) return;
    setBusy(true);
    setShowConfirm(false);
    setStep(hasMapStep ? 4 : 4);
    try {
      const r = await api.post(
        `/platform/companies/${cid}/catalog/imports/${previewRun.id}/commit`,
        { confirm: "COMMIT" },
      );
      setCommitResult(r.data);
      setStep(hasMapStep ? 5 : 5);
      toast.success(
        `Committed ${r.data.committed} rows (${r.data.memberships_written} memberships).`,
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
      setStep(hasMapStep ? 3 : 3);
    } finally {
      setBusy(false);
    }
  }, [previewRun, confirmAcknowledged, cid, hasMapStep]);

  const downloadClientInvalidCsv = useCallback(() => {
    const headers = ["line", "errors", ...ALL_FIELDS];
    const rows = clientInvalid.map(({ line, errors, row }) => ({
      line,
      errors: errors.join("; "),
      ...row,
    }));
    downloadBlob(
      makeCsv(headers, rows),
      `shelflife_invalid_rows_${cid}.csv`,
    );
    toast.success("Invalid rows CSV downloaded.");
  }, [clientInvalid, cid]);

  const downloadServerInvalidCsv = useCallback(() => {
    const bad = previewRows.filter(
      (r) =>
        r.classification === "rejected" || r.classification === "needs_review",
    );
    if (!bad.length) {
      toast.info("No invalid rows to download.");
      return;
    }
    const headers = [
      "classification",
      "review_decision",
      "errors",
      ...ALL_FIELDS,
    ];
    const rows = bad.map((r) => ({
      classification: r.classification,
      review_decision: r.review_decision,
      errors: (r.diagnostics || []).join("; "),
      barcode: r.barcode || "",
      product_name: r.product_name || "",
      department_name_raw: r.department_name_raw || "",
      internal_code: r.internal_code || "",
      source_item_id: r.source_item_id || "",
      category: r.category || "",
      subcategory: r.subcategory || "",
      image_url: r.image_url || "",
    }));
    downloadBlob(
      makeCsv(headers, rows),
      `shelflife_invalid_rows_${cid}_${previewRun?.id || "run"}.csv`,
    );
    toast.success("Invalid rows CSV downloaded.");
  }, [previewRows, cid, previewRun]);

  // ---- Render ----

  if (companyError) {
    return (
      <div data-testid="wizard-tenant-error" className="py-12">
        <EmptyState
          icon={<AlertTriangle size={32} />}
          title="Cannot load target tenant"
          description={companyError}
          action={
            <Button tier="primary" onClick={() => nav("/platform")}>
              Back to tenants
            </Button>
          }
        />
      </div>
    );
  }

  const approvedCountVal = previewRows.filter(
    (r) =>
      r.review_decision === "approve" &&
      (r.classification === "add" || r.classification === "update"),
  ).length;

  const counts = validateResult?.counts || {};
  const invalidServer = previewRows.filter(
    (r) =>
      r.classification === "rejected" || r.classification === "needs_review",
  );

  return (
    <div data-testid="catalog-import-wizard">
      <div className="mb-4">
        <Link
          to={`/platform/companies/${cid}/catalog/imports`}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-ink"
          data-testid="wizard-back"
        >
          <ArrowLeft size={14} />
          Back to imports
        </Link>
      </div>

      <PageHeader
        eyebrow={
          <span className="font-mono text-[11px]">
            tenant: <span data-testid="wizard-tenant-id">{cid}</span>
          </span>
        }
        title="New catalog import"
        subtitle={
          <>
            Importing into:{" "}
            <strong data-testid="wizard-tenant-name">
              {company?.name || "…"}
            </strong>
          </>
        }
      />

      <Stepper step={step} hasMapStep={hasMapStep} />

      {/* Step 0 — Upload (three-section guide) */}
      {step === 0 && (
        <>
          {/* Target-tenant reminder banner */}
          <div
            className="mb-6 px-4 py-3 rounded-card bg-amber-50 border border-amber-200 text-amber-900 flex items-start gap-3"
            data-testid="wizard-target-banner"
          >
            <AlertTriangle
              size={16}
              className="shrink-0 mt-0.5"
              strokeWidth={2.25}
            />
            <div className="text-sm">
              You are preparing an import for{" "}
              <strong>{company?.name || cid}</strong>. Products will be added
              to <strong>this tenant only</strong>.
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[5fr_4fr] gap-6 mb-6">
            {/* Left column — Prepare + Upload/paste */}
            <div className="space-y-6">
              {/* Section 1 — Prepare your catalog file */}
              <Card
                variant="raised"
                className="p-6"
                data-testid="section-prepare"
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground mt-1 shrink-0">
                    01
                  </div>
                  <div>
                    <h2 className="font-display font-black text-lg">
                      Prepare your catalog file
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Use the template to avoid column-name and
                      department-spelling errors.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mb-5">
                  <div>
                    <div className="data-sku mb-2">Required fields</div>
                    <ul className="space-y-1">
                      {REQUIRED_FIELDS.map((f) => (
                        <li
                          key={f}
                          className="flex items-center gap-2 text-sm font-mono"
                        >
                          <CheckCircle
                            size={12}
                            className="text-emerald-700 shrink-0"
                          />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="data-sku mb-2">Optional fields</div>
                    <ul className="space-y-1">
                      {OPTIONAL_FIELDS.map((f) => (
                        <li
                          key={f}
                          className="flex items-center gap-2 text-sm font-mono text-muted-foreground"
                        >
                          <span className="size-2 rounded-full bg-muted-foreground/30 shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <Button
                  tier="primary"
                  onClick={handleDownloadTemplate}
                  data-testid="wizard-download-template"
                >
                  <Download size={14} /> Download CSV template
                </Button>

                {tenantDepts.length > 0 && (
                  <p className="text-[11px] text-muted-foreground mt-3 font-mono">
                    Sample rows use{" "}
                    <strong>{company?.name || "this tenant"}</strong>&apos;s
                    current departments.
                  </p>
                )}

                {/* Collapsible template preview */}
                <details
                  className="mt-4 group"
                  data-testid="wizard-template-preview-details"
                >
                  <summary className="cursor-pointer font-mono text-[11px] uppercase tracking-wider text-muted-foreground hover:text-ink select-none">
                    <span className="group-open:hidden">
                      View template example ▾
                    </span>
                    <span className="hidden group-open:inline">
                      Hide template example ▴
                    </span>
                  </summary>
                  <pre
                    className="mt-3 text-[11px] font-mono whitespace-pre overflow-x-auto bg-paper-sunk rounded-button p-3 border border-border/40 max-h-48"
                    data-testid="wizard-template-preview"
                  >
                    {templateCsv}
                  </pre>
                </details>
              </Card>

              {/* Section 2 — Upload or paste CSV */}
              <Card
                variant="raised"
                className="p-6"
                data-testid="section-upload"
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground mt-1 shrink-0">
                    02
                  </div>
                  <div>
                    <h2 className="font-display font-black text-lg">
                      Upload or paste CSV
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Choose either option. We&apos;ll auto-detect common
                      columns.
                    </p>
                  </div>
                </div>

                {/* Two clear input options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <label
                    className="flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-card border-2 border-dashed border-border hover:border-brand hover:bg-paper-2/60 cursor-pointer transition-colors text-center"
                    data-testid="wizard-csv-file-label"
                  >
                    <Upload size={20} className="text-brand" strokeWidth={2} />
                    <span className="font-display font-bold text-sm">
                      Upload .csv file
                    </span>
                    <span className="text-[11px] font-mono text-muted-foreground">
                      Drag-drop coming soon — click to browse
                    </span>
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      onChange={onFile}
                      className="hidden"
                      data-testid="wizard-csv-file"
                    />
                  </label>
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="wizard-csv-textarea-id"
                      className="data-sku"
                    >
                      Paste CSV content here
                    </label>
                    <textarea
                      id="wizard-csv-textarea-id"
                      value={csvText}
                      onChange={(e) => setCsvText(e.target.value)}
                      className="w-full font-mono text-xs border border-border rounded-button p-3 min-h-[148px] flex-1"
                      placeholder={`barcode,product_name,department_name_raw\n8410076000001,Cheese Wheel,Cheese\n...`}
                      data-testid="wizard-csv-textarea"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  <Button
                    tier="primary"
                    onClick={handleParseAndAdvance}
                    disabled={busy || !csvText.trim()}
                    data-testid="wizard-validate-button"
                  >
                    {busy ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <CheckCircle size={14} />
                    )}
                    Continue
                  </Button>
                  <span className="font-mono text-[11px] text-muted-foreground">
                    Nothing is committed yet — you&apos;ll review the preview
                    before any product is created.
                  </span>
                </div>
              </Card>
            </div>

            {/* Right column — What happens next */}
            <Card
              variant="raised"
              className="p-6 self-start lg:sticky lg:top-6"
              data-testid="section-whats-next"
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground mt-1 shrink-0">
                  03
                </div>
                <div>
                  <h2 className="font-display font-black text-lg">
                    What happens next
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    The wizard walks you through the full import safely.
                  </p>
                </div>
              </div>
              <ol className="space-y-3 text-sm">
                {[
                  "ShelfLife auto-detects your CSV columns.",
                  "Map any columns we can't auto-match (optional step).",
                  "Rows are validated and any invalid rows are flagged.",
                  "You review a preview run — nothing is committed yet.",
                  "Commit only after explicit confirmation. Imported products land in this tenant only.",
                ].map((line, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3"
                    data-testid={`whats-next-step-${i + 1}`}
                  >
                    <span className="inline-flex items-center justify-center size-6 shrink-0 rounded-full bg-paper-sunk font-display font-black text-xs">
                      {i + 1}
                    </span>
                    <span className="text-ink/90">{line}</span>
                  </li>
                ))}
              </ol>
              <div className="mt-4 pt-4 border-t border-border/40 text-[11px] font-mono text-muted-foreground">
                Target tenant:{" "}
                <strong className="text-ink">{company?.name || cid}</strong>
              </div>
            </Card>
          </div>
        </>
      )}

      {/* Step 1 — Column mapping */}
      {step === 1 && hasMapStep && (
        <Card
          variant="raised"
          className="p-6 mb-6"
          data-testid="wizard-mapping-card"
        >
          <div className="font-display font-black text-lg mb-2">
            Map your columns
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            We could not auto-match every column. Pick the supported field for
            each incoming column. Required fields:{" "}
            <strong>{REQUIRED_FIELDS.join(", ")}</strong>.
          </p>
          <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-3 max-w-2xl mb-4">
            <div className="data-sku">Your column</div>
            <div className="data-sku">ShelfLife field</div>
            {parsed.headers.map((h) => (
              <React.Fragment key={h}>
                <div
                  className="font-mono text-xs truncate"
                  title={h}
                  data-testid={`map-source-${h}`}
                >
                  {h}
                </div>
                <select
                  className="border border-border rounded-button px-2 py-1 text-xs font-mono"
                  value={mapping[h] || "ignore"}
                  onChange={(e) =>
                    setMapping((prev) => ({ ...prev, [h]: e.target.value }))
                  }
                  data-testid={`map-select-${h}`}
                >
                  <option value="ignore">— ignore —</option>
                  {ALL_FIELDS.map((f) => {
                    // Disallow picking the same field twice (except for
                    // the currently-selected column).
                    const usedElsewhere = Object.entries(mapping).some(
                      ([k, v]) => k !== h && v === f,
                    );
                    return (
                      <option
                        key={f}
                        value={f}
                        disabled={usedElsewhere}
                      >
                        {f}
                        {REQUIRED_FIELDS.includes(f) ? " (required)" : ""}
                      </option>
                    );
                  })}
                </select>
              </React.Fragment>
            ))}
          </div>

          {(() => {
            const missing = mappingMissingRequired(mapping);
            if (missing.length === 0) return null;
            return (
              <div
                className="text-xs text-rose-900 bg-rose-50 p-2 rounded-button mb-3 font-mono"
                data-testid="wizard-mapping-missing"
              >
                <AlertTriangle size={12} className="inline mr-1" /> Map the
                required field
                {missing.length === 1 ? "" : "s"}: {missing.join(", ")}
              </div>
            );
          })()}

          <div className="flex items-center gap-3 flex-wrap">
            <Button
              tier="primary"
              onClick={() => handleValidate()}
              disabled={busy || mappingMissingRequired(mapping).length > 0}
              data-testid="wizard-mapping-validate"
            >
              {busy ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <CheckCircle size={14} />
              )}
              Validate with this mapping
            </Button>
            <Button
              tier="secondary"
              onClick={() => setStep(0)}
              disabled={busy}
              data-testid="wizard-mapping-back"
            >
              Back
            </Button>
          </div>
        </Card>
      )}

      {/* Step 2 — Validation summary + invalid rows */}
      {validateResult && step >= 2 && (
        <Card variant="raised" className="p-6 mb-6">
          <div className="font-display font-black text-lg mb-3">
            Validation summary
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <CountTile
              label="Rows"
              value={validateResult.row_count}
              testid="validate-count-total"
            />
            <CountTile
              label="Add"
              value={counts.add}
              tone="ok"
              testid="validate-count-add"
            />
            <CountTile
              label="Update"
              value={counts.update}
              tone="warn"
              testid="validate-count-update"
            />
            <CountTile
              label="Unchanged"
              value={counts.unchanged}
              testid="validate-count-unchanged"
            />
            <CountTile
              label="Needs review"
              value={counts.needs_review}
              tone="warn"
              testid="validate-count-needs-review"
            />
            <CountTile
              label="Rejected"
              value={counts.rejected}
              tone="danger"
              testid="validate-count-rejected"
            />
          </div>

          {/* Client-side invalid rows (cheap pre-check) */}
          {step === 2 && clientInvalid.length > 0 && (
            <div className="mt-5" data-testid="wizard-invalid-rows">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <div className="font-display font-black text-sm">
                  {clientInvalid.length} invalid row
                  {clientInvalid.length === 1 ? "" : "s"} detected
                </div>
                <Button
                  tier="secondary"
                  size="sm"
                  onClick={downloadClientInvalidCsv}
                  data-testid="wizard-download-invalid-rows"
                >
                  <Download size={14} /> Download invalid rows CSV
                </Button>
              </div>
              <div className="overflow-x-auto bg-rose-50/40 rounded-card max-h-60">
                <div className="min-w-[640px] grid grid-cols-[60px_1fr_2fr] gap-2 px-3 py-2 data-sku">
                  <div>Line</div>
                  <div>Errors</div>
                  <div>Row</div>
                </div>
                {clientInvalid.slice(0, 50).map(({ line, errors, row }) => (
                  <div
                    key={line}
                    className="min-w-[640px] grid grid-cols-[60px_1fr_2fr] gap-2 px-3 py-1.5 border-t border-rose-200/40 text-xs"
                  >
                    <div className="font-mono">{line}</div>
                    <div className="text-rose-900">
                      {errors.join("; ")}
                    </div>
                    <div className="font-mono text-[11px] truncate">
                      {row.barcode || "?"}: {row.product_name || ""}
                    </div>
                  </div>
                ))}
                {clientInvalid.length > 50 && (
                  <div className="text-center font-mono text-[11px] text-muted-foreground py-2">
                    Showing first 50 of {clientInvalid.length} invalid rows.
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <Button
                tier="primary"
                onClick={handlePreview}
                disabled={busy || !counts.staged}
                data-testid="wizard-preview-button"
              >
                {busy ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <PackagePlus size={14} />
                )}
                Create preview run
              </Button>
              <Button
                tier="secondary"
                onClick={() => {
                  setStep(0);
                  setValidateResult(null);
                  setClientInvalid([]);
                }}
                disabled={busy}
                data-testid="wizard-revalidate-button"
              >
                Edit CSV
              </Button>
              <span className="font-mono text-[11px] text-muted-foreground">
                Preview persists staged rows but does NOT write Products yet.
              </span>
            </div>
          )}
        </Card>
      )}

      {/* Step 3 — Preview rows + Cancel + Commit launcher */}
      {step >= 3 && previewRun && (
        <Card variant="raised" className="p-6 mb-6">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
            <div>
              <div className="font-display font-black text-lg">
                Preview rows
              </div>
              <div className="font-mono text-[11px] text-muted-foreground">
                run_id: {previewRun.id} · status:{" "}
                <span className="text-ink">{previewRun.status}</span>
              </div>
            </div>
            {step === 3 && (
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  tier="secondary"
                  onClick={handleCancelRun}
                  disabled={busy}
                  data-testid="wizard-cancel-run-button"
                >
                  <Ban size={14} /> Cancel run
                </Button>
                <Button
                  tier="primary"
                  onClick={openCommitModal}
                  disabled={busy || !approvedCountVal}
                  data-testid="wizard-commit-button"
                >
                  <CheckCircle size={14} />
                  Commit to {company?.name || "tenant"}
                </Button>
              </div>
            )}
          </div>
          <div className="text-xs text-amber-900 bg-amber-50 p-3 rounded-button mb-3 inline-flex items-start gap-2">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>
              This will import <strong>{approvedCountVal}</strong> approved
              row
              {approvedCountVal === 1 ? "" : "s"} into{" "}
              <strong>{company?.name || cid}</strong>&apos;s catalog. Commit
              is idempotent — re-clicking is a no-op once committed.
            </span>
          </div>

          {invalidServer.length > 0 && (
            <div className="mb-3">
              <Button
                tier="secondary"
                size="sm"
                onClick={downloadServerInvalidCsv}
                data-testid="wizard-download-server-invalid"
              >
                <Download size={14} /> Download {invalidServer.length}{" "}
                invalid row{invalidServer.length === 1 ? "" : "s"} CSV
              </Button>
            </div>
          )}

          <div className="overflow-x-auto">
            <div className="min-w-[820px] grid grid-cols-[1fr_180px_180px_120px_120px] gap-3 px-3 py-2 bg-paper-sunk rounded-card data-sku">
              <div>Product name</div>
              <div>Barcode</div>
              <div>Department</div>
              <div>Classification</div>
              <div>Decision</div>
            </div>
            {previewRows.slice(0, 200).map((r) => (
              <div
                key={r.id}
                className="min-w-[820px] grid grid-cols-[1fr_180px_180px_120px_120px] gap-3 px-3 py-2 border-b border-border/30 text-sm"
                data-testid={`preview-row-${r.id}`}
              >
                <div className="truncate" title={r.product_name}>
                  {r.product_name || "—"}
                </div>
                <div className="font-mono text-xs truncate">
                  {r.barcode || "—"}
                </div>
                <div className="font-mono text-xs truncate">
                  {r.department_name_raw || "—"}
                  {!r.department_id_resolved && (
                    <span className="text-amber-700 ml-1">⚠</span>
                  )}
                </div>
                <div>
                  <ClassificationBadge kind={r.classification} />
                </div>
                <div className="font-mono text-[11px] uppercase">
                  {r.review_decision}
                </div>
              </div>
            ))}
            {previewRows.length > 200 && (
              <div className="text-center font-mono text-[11px] text-muted-foreground py-3">
                Showing first 200 of {previewRows.length} rows.
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Step 5 — Commit success */}
      {step === 5 && commitResult && (
        <Card
          variant="raised"
          className="p-6 mb-6"
          data-testid="commit-success-card"
        >
          <div className="flex items-start gap-3 mb-4">
            <CheckCircle
              size={28}
              className="text-emerald-700 shrink-0 mt-1"
            />
            <div>
              <div className="font-display font-black text-xl">
                Import committed
              </div>
              <p className="text-sm text-muted-foreground">
                Products are now live in{" "}
                <strong>{company?.name || cid}</strong>&apos;s catalog.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <CountTile
              label="Committed"
              value={commitResult.committed}
              tone="ok"
              testid="commit-count-committed"
            />
            <CountTile
              label="Memberships"
              value={commitResult.memberships_written}
              tone="ok"
              testid="commit-count-memberships"
            />
            <CountTile
              label="Skipped"
              value={commitResult.skipped_conflict}
              testid="commit-count-skipped"
            />
            <CountTile
              label="Failed"
              value={commitResult.failed || 0}
              tone={commitResult.failed ? "danger" : undefined}
              testid="commit-count-failed"
            />
          </div>
          <div className="font-mono text-[11px] text-muted-foreground mb-4">
            run_id: {commitResult.run_id || previewRun?.id}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              tier="primary"
              onClick={() =>
                nav(
                  `/platform/companies/${cid}/catalog/imports/${
                    commitResult.run_id || previewRun?.id
                  }`,
                )
              }
              data-testid="commit-view-run-button"
            >
              View import run
            </Button>
            <Button
              tier="secondary"
              onClick={() => nav(`/platform/companies/${cid}`)}
              data-testid="commit-back-tenant"
            >
              Back to {company?.name || "tenant"}
            </Button>
            <Button
              tier="secondary"
              onClick={() => {
                setStep(0);
                setHasMapStep(false);
                setCsvText("");
                setParsed({ headers: [], rows: [], error: null });
                setMapping({});
                setValidateResult(null);
                setClientInvalid([]);
                setPreviewRun(null);
                setPreviewRows([]);
                setCommitResult(null);
              }}
              data-testid="commit-new-import-button"
            >
              <FileSpreadsheet size={14} /> Start another import
            </Button>
          </div>
        </Card>
      )}

      {/* Commit confirmation modal */}
      {showConfirm && previewRun && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4"
          data-testid="commit-confirm-modal"
        >
          <Card
            variant="raised"
            className="max-w-lg w-full p-6 relative"
          >
            <button
              onClick={() => setShowConfirm(false)}
              className="absolute top-3 right-3 text-muted-foreground hover:text-ink"
              data-testid="commit-confirm-close"
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <div className="font-display font-black text-xl mb-1">
              Confirm catalog import
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              You&apos;re about to commit products into the{" "}
              <strong data-testid="commit-confirm-tenant-name">
                {company?.name || cid}
              </strong>{" "}
              catalog only. This action is idempotent but cannot easily be
              undone.
            </p>
            <div className="space-y-2 mb-4 font-mono text-xs bg-paper-sunk p-3 rounded-card">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Target tenant</span>
                <strong>{company?.name || cid}</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">run_id</span>
                <span className="truncate">{previewRun.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total rows</span>
                <strong data-testid="commit-confirm-total">
                  {counts.staged || previewRows.length}
                </strong>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Add</span>
                <strong data-testid="commit-confirm-add">
                  {counts.add ?? 0}
                </strong>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Update</span>
                <strong data-testid="commit-confirm-update">
                  {counts.update ?? 0}
                </strong>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Invalid (skipped)</span>
                <strong data-testid="commit-confirm-invalid">
                  {(counts.rejected ?? 0) + (counts.needs_review ?? 0)}
                </strong>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  Memberships expected
                </span>
                <strong data-testid="commit-confirm-memberships">
                  {(counts.add ?? 0) + (counts.update ?? 0)}
                </strong>
              </div>
            </div>
            <div className="text-xs text-amber-900 bg-amber-50 p-3 rounded-button mb-4 inline-flex items-start gap-2">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>
                This will import products into{" "}
                <strong>{company?.name || cid}</strong> only — no other tenant
                is affected.
              </span>
            </div>
            <label className="flex items-start gap-2 mb-4 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={confirmAcknowledged}
                onChange={(e) => setConfirmAcknowledged(e.target.checked)}
                data-testid="commit-confirm-checkbox"
                className="mt-1"
              />
              <span>
                I confirm I am importing into{" "}
                <strong>{company?.name || cid}</strong>.
              </span>
            </label>
            <div className="flex items-center gap-2 justify-end flex-wrap">
              <Button
                tier="secondary"
                onClick={() => setShowConfirm(false)}
                disabled={busy}
                data-testid="commit-confirm-cancel"
              >
                Cancel
              </Button>
              <Button
                tier="primary"
                onClick={handleCommit}
                disabled={busy || !confirmAcknowledged}
                data-testid="commit-confirm-confirm"
              >
                {busy ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <CheckCircle size={14} />
                )}
                Commit to {company?.name || "tenant"}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
